/**
 * useDragSortList — the mousedown/mousemove/mouseup wiring behind a plain "drag a card to
 * reorder this vertical list" gesture, extracted so InsightsPanel, TriagePanel, and InsightsPage
 * (previously three copies of the same ~50 lines) share one implementation. Deliberately
 * content-agnostic, same spirit as useConnectDrag: it only tracks which rendered row (tagged via
 * DRAG_SORT_INDEX_ATTR — see utils/dragSortList.ts) is being dragged and where the pointer
 * currently is, and fires `onReorder` on mouseup with the resolved (fromIndex, toIndex) pair. It
 * does not render anything — callers still build their own row markup (via
 * dragSortItemClassName/dragSortTranslateStyle) and their own floating ghost, since those differ
 * by which card component each list renders.
 *
 * NOT used by PlanPage's own Plan/Triage list columns: that page's drag gesture can also end on
 * the Timeline canvas rather than just reordering its origin list, which needs a richer state
 * machine than this hook provides. PlanPage instead reuses this module's pure functions directly
 * (see utils/dragSortList.ts's own doc comment) for the parts that do overlap.
 */
import { useCallback, useRef, useState } from 'react';
import { DRAG_SORT_INDEX_ATTR, DragSortState, getDragSortTargetIndex, getDragSortTranslateY } from 'utils/dragSortList';

export interface UseDragSortListOptions {
    /** Number of items in the full list being reordered (not just what's currently rendered,
     * e.g. after a filter) — used only to clamp the resolved target index. */
    itemCount: number;
    /** Container to scan for rendered `[data-drag-sort-index]` rows. */
    containerRef: React.RefObject<HTMLElement | null>;
    /** Fired on mouseup, only if the item actually moved. */
    onReorder: (fromIndex: number, toIndex: number) => void;
    /** The list's own CSS `gap` between rows (px) — see getDragSortTranslateY's doc comment. */
    gap: number;
}

export interface UseDragSortListResult {
    /** Non-null for the duration of a drag; null otherwise. */
    dragState: DragSortState | null;
    /** Attach to a row's drag handle's onMouseDown, passing that row's current index. */
    beginDrag: (e: React.MouseEvent, index: number) => void;
    /** Vertical offset (px) row `index` should currently be translated by — 0 outside a drag. */
    getTranslateY: (index: number) => number;
}

export default function useDragSortList({ itemCount, containerRef, onReorder, gap }: UseDragSortListOptions): UseDragSortListResult {
    const [dragState, setDragState] = useState<DragSortState | null>(null);
    // Mirror dragState's fromIndex/toIndex in refs so the window-level mousemove/mouseup
    // closures below (registered once per drag) always read the latest target instead of the
    // stale value captured when they were attached.
    const fromIndexRef = useRef<number>(-1);
    const toIndexRef = useRef<number>(-1);

    const beginDrag = useCallback(
        (e: React.MouseEvent, index: number) => {
            e.preventDefault();
            if (index === -1) return;

            const container = containerRef.current;
            if (!container) return;
            const rows = container.querySelectorAll<HTMLElement>(`[${DRAG_SORT_INDEX_ATTR}]`);
            const rowEl = Array.from(rows).find((el) => Number(el.getAttribute(DRAG_SORT_INDEX_ATTR)) === index);
            if (!rowEl) return;

            const rect = rowEl.getBoundingClientRect();
            fromIndexRef.current = index;
            toIndexRef.current = index;

            setDragState({
                fromIndex: index,
                toIndex: index,
                mouseY: e.clientY,
                offsetY: e.clientY - rect.top,
                cardHeight: rect.height,
                ghostX: rect.left,
                ghostWidth: rect.width,
            });

            const onMouseMove = (mv: globalThis.MouseEvent) => {
                const currentContainer = containerRef.current;
                if (!currentContainer) return;

                const allRows = Array.from(currentContainer.querySelectorAll<HTMLElement>(`[${DRAG_SORT_INDEX_ATTR}]`));
                const otherItems = allRows
                    .map((el) => ({ index: Number(el.getAttribute(DRAG_SORT_INDEX_ATTR)), rect: el.getBoundingClientRect() }))
                    .filter((entry) => entry.index !== fromIndexRef.current)
                    .map((entry) => ({ index: entry.index, top: entry.rect.top, height: entry.rect.height }));

                const newToIndex = getDragSortTargetIndex(mv.clientY, otherItems, itemCount);
                toIndexRef.current = newToIndex;
                setDragState((prev) => (prev ? { ...prev, toIndex: newToIndex, mouseY: mv.clientY } : null));
            };

            const onMouseUp = () => {
                if (fromIndexRef.current !== toIndexRef.current) {
                    onReorder(fromIndexRef.current, toIndexRef.current);
                }
                setDragState(null);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },
        [containerRef, itemCount, onReorder],
    );

    const getTranslateY = useCallback((index: number) => getDragSortTranslateY(dragState, index, gap), [dragState, gap]);

    return { dragState, beginDrag, getTranslateY };
}
